import { ArrowPathIcon } from '@heroicons/react/16/solid';
import { InlineEditableValue } from '@/components/ui/InlineEditableValue';
import { ProjectionInfoButton } from './ProjectionInfoButton';
import type { NetWorthProjectionResponse } from '@/api/types';

interface ProjectionExplanationProps {
    /** Projection data from API */
    projectionData: NetWorthProjectionResponse;
    /** User override for return rate (null = use calculated/default) */
    returnRate: number | null;
    /** User override for contribution (null = use calculated/default) */
    contribution: number | null;
    /** User override for yearly debt change (null = use calculated trend) */
    debtChange: number | null;
    /** Projection years */
    years: number;
    /** Callback when return rate changes */
    onReturnRateChange: (rate: number) => void;
    /** Callback when contribution changes */
    onContributionChange: (amount: number) => void;
    /** Callback when yearly debt change changes */
    onDebtChangeChange: (amount: number) => void;
    /** Callback when years changes */
    onYearsChange: (years: number) => void;
    /** Callback to reset to calculated values */
    onReset: () => void;
    /** True while fresh projection data is being fetched */
    isRefreshing?: boolean;
    /**
     * Compositional mode: FI on with a real subset. The whole-portfolio return
     * rate no longer drives growth (the investable rate does), so it is rendered
     * as display-only reference text instead of an editable knob.
     */
    compositionalMode?: boolean;
    /** FI investable-rate override (null = derived); counted toward hasModifications */
    fiInvestableReturnRate?: number | null;
    /** Whether the post-FI withdrawal simulation is on; counted toward hasModifications */
    fiSimulateWithdrawal?: boolean;
    /** Inflation-rate override (null = server default); counted toward hasModifications */
    fiInflationRate?: number | null;
}

/**
 * Explanatory text with embedded editable values for the projection settings.
 * Displays a human-readable sentence explaining how the projection is calculated.
 */
export function ProjectionExplanation({
    projectionData,
    returnRate,
    contribution,
    debtChange,
    years,
    onReturnRateChange,
    onContributionChange,
    onDebtChangeChange,
    onYearsChange,
    onReset,
    isRefreshing = false,
    compositionalMode = false,
    fiInvestableReturnRate = null,
    fiSimulateWithdrawal = false,
    fiInflationRate = null,
}: ProjectionExplanationProps) {
    const {
        calculatedReturnRate,
        calculatedContribution,
        appliedReturnRate,
        appliedContribution,
        appliedYearlyDebtChange,
        currency,
        historicalYears,
        defaultProjectionYears,
    } = projectionData;

    // Check if user has modified any values
    const hasModifications = returnRate !== null || contribution !== null || debtChange !== null || years !== defaultProjectionYears || fiInvestableReturnRate !== null || fiSimulateWithdrawal || fiInflationRate !== null;

    // Determine the display values (user override or applied value from API)
    const displayReturnRate = returnRate ?? appliedReturnRate;
    const displayContribution = contribution ?? appliedContribution;

    // Determine if we have calculated values (from historical data)
    const hasCalculatedReturn = calculatedReturnRate !== null && calculatedReturnRate !== undefined;
    const hasCalculatedContribution = calculatedContribution !== null && calculatedContribution !== undefined;

    // Detect overrides the backend clamped to its allowed bounds, so the
    // user is never silently projected with a different value than entered.
    // While a refetch is in flight the applied values still belong to the
    // PREVIOUS request, so comparing would flash a bogus "adjusted" note.
    const returnRateAdjusted = !isRefreshing && returnRate !== null && Math.abs(appliedReturnRate - returnRate) > 0.0005;
    const contributionAdjusted = !isRefreshing && contribution !== null && Math.abs(appliedContribution - contribution) > 0.5;
    const debtChangeAdjusted = !isRefreshing && debtChange !== null && Math.abs((appliedYearlyDebtChange ?? 0) - debtChange) > 0.5;

    // Cached responses from before this field existed may omit it
    const displayDebtChange = debtChange ?? appliedYearlyDebtChange ?? 0;

    const formatMoney = (value: number) =>
        new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency,
            maximumFractionDigits: 0,
        }).format(value);

    return (
        <div className="flex items-center gap-4">
            {/* Text content */}
            <div className="flex-1 space-y-1">
                {/* First sentence: historical data and return rate */}
                <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                    {compositionalMode ? (
                        <>
                            Based on your {historicalYears} years of data (converted to {currency}), your whole portfolio has returned{' '}
                            <strong className="text-gray-900 dark:text-white">
                                {(displayReturnRate * 100).toFixed(1)}%
                            </strong>{' '}
                            annually (blended — shown for reference; growth is driven by the investable rate in the Financial independence panel).
                        </>
                    ) : hasCalculatedReturn ? (
                        <>
                            Based on your {historicalYears} years of data (converted to {currency}), your investments have returned{' '}
                            <InlineEditableValue
                                value={displayReturnRate}
                                onChange={onReturnRateChange}
                                format="percent"
                                min={-0.5}
                                max={0.5}
                                step={0.1}
                            />{' '}
                            annually.
                        </>
                    ) : (
                        <>
                            Using a conservative{' '}
                            <InlineEditableValue
                                value={displayReturnRate}
                                onChange={onReturnRateChange}
                                format="percent"
                                min={-0.5}
                                max={0.5}
                                step={0.1}
                            />{' '}
                            annual return estimate.
                        </>
                    )}
                </p>

                {/* Second sentence: contributions and projection period */}
                <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                    {hasCalculatedContribution && calculatedContribution !== 0 ? (
                        <>
                            With{' '}
                            <InlineEditableValue
                                value={displayContribution}
                                onChange={onContributionChange}
                                format="currency"
                                currency={currency}
                                min={-1000000000}
                                max={1000000000}
                                step={1000}
                            />{' '}
                            in net yearly cash flow (negative = withdrawal),
                        </>
                    ) : (
                        <>
                            With{' '}
                            <InlineEditableValue
                                value={displayContribution}
                                onChange={onContributionChange}
                                format="currency"
                                currency={currency}
                                min={-1000000000}
                                max={1000000000}
                                step={1000}
                            />{' '}
                            net yearly cash flow assumed (negative = withdrawal),
                        </>
                    )}
                    {(displayDebtChange !== 0 || debtChange !== null) && (
                        <>
                            {' '}and debts changing by{' '}
                            <InlineEditableValue
                                value={displayDebtChange}
                                onChange={onDebtChangeChange}
                                format="currency"
                                currency={currency}
                                min={-1000000000}
                                max={1000000000}
                                step={500}
                            />
                            /year{debtChange === null ? ' (your historical trend)' : ''},
                        </>
                    )}{' '}
                    here's your projected net worth over the next{' '}
                    <InlineEditableValue
                        value={years}
                        onChange={onYearsChange}
                        format="years"
                        min={1}
                        max={100}
                        step={5}
                    />{' '}
                    years.
                </p>

                {/* Real-terms note when the post-FI withdrawal simulation is on. */}
                {fiSimulateWithdrawal && (
                    <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                        Real-terms mode is on: every value is shown in today's money, and once you
                        reach FI your contributions stop and your annual expenses are withdrawn each
                        year. See the Financial independence panel for the inflation assumption and
                        the year withdrawals begin.
                    </p>
                )}

                {(returnRateAdjusted || contributionAdjusted || debtChangeAdjusted) && (
                    <p role="status" className="text-xs text-amber-600 dark:text-amber-400">
                        {returnRateAdjusted && (
                            <>Return rate was adjusted to {(appliedReturnRate * 100).toFixed(1)}% (allowed range −100% to 100%).{' '}</>
                        )}
                        {contributionAdjusted && (
                            <>Contribution was adjusted to {formatMoney(appliedContribution)} (limit ±{formatMoney(1000000000)}).{' '}</>
                        )}
                        {debtChangeAdjusted && (
                            <>Debt change was adjusted to {formatMoney(appliedYearlyDebtChange ?? 0)} (limit ±{formatMoney(1000000000)}).</>
                        )}
                    </p>
                )}
            </div>

            {/* Controls - right aligned, vertically centered */}
            <div className="shrink-0 flex items-center gap-1">
                {hasModifications && (
                    <button
                        type="button"
                        onClick={onReset}
                        title="Reset to calculated values"
                        className="p-2 rounded-lg
                            text-gray-400 hover:text-accent dark:text-gray-500 dark:hover:text-accent
                            hover:bg-gray-100 dark:hover:bg-gray-700
                            transition-colors"
                    >
                        <ArrowPathIcon className="w-5 h-5" />
                    </button>
                )}
                <ProjectionInfoButton />
            </div>
        </div>
    );
}
